import { useState, useEffect, useCallback } from "react";
import { ReviewCard } from "./ReviewCard";
import { ReviewForm } from "./ReviewForm";
import { Icon } from "./Icon.jsx";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";

// Reviews & Ratings + Questions & Answers for a single product — self-contained so it can
// drop into any surface (the full /product/:id page AND the storefront quick-view modal) and
// stay identical. Fetches its own data from the existing /api/reviews/* and /api/qa/* endpoints.
//   productId       — required
//   storeId         — the product's store (passed to ReviewForm)
//   customer        — logged-in customer (null = signed out; gates review/question posting)
//   session         — seller session (optional); used with productOwnerId to let the OWNER answer
//   productOwnerId  — the product's user_id; owner === session.userId can answer questions
export function ProductReviewsQNA({ productId, storeId, customer, session = null, productOwnerId = null, qaEnabled = true }) {
  const [reviews,      setReviews]      = useState([]);
  const [avgRating,    setAvgRating]    = useState(0);
  const [qas,          setQas]          = useState([]);
  const [question,     setQuestion]     = useState("");
  const [guestName,    setGuestName]    = useState("");   // asker's name when not signed in
  const [submittingQ,  setSubmittingQ]  = useState(false);
  const [replyTexts,   setReplyTexts]   = useState({});
  const [submittingA,  setSubmittingA]  = useState({});
  const [qaError,      setQaError]      = useState("");
  const [qaOffByServer, setQaOffByServer] = useState(false);   // the Q&A fetch told us this store switched Q&A off
  const qaHidden = !qaEnabled || qaOffByServer;                    // seller turned Questions & Answers off in Settings

  const isOwner = !!session?.userId && !!productOwnerId && session.userId === productOwnerId;

  const fetchReviews = useCallback(async () => {
    if (!productId) return;
    try {
      const [reviewsRes, avgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/reviews/product/${productId}`),
        fetch(`${BACKEND_URL}/api/reviews/avg/${productId}`),
      ]);
      if (reviewsRes.ok) setReviews(await reviewsRes.json().then(d => Array.isArray(d) ? d : []));
      if (avgRes.ok)     setAvgRating((await avgRes.json()).average_rating || 0);
    } catch (e) { console.error("Error fetching reviews:", e); }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
    const fetchQAs = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/qa/product/${productId}`);
        if (res.headers.get("X-QA-Enabled") === "0") setQaOffByServer(true);
        if (res.ok) setQas(await res.json().then(d => Array.isArray(d) ? d : []));
      } catch (e) { console.error("Error fetching QAs:", e); }
    };
    if (productId && qaEnabled) fetchQAs();
  }, [productId, fetchReviews, qaEnabled]);

  const handleAskQuestion = async () => {
    if (!question.trim()) return;
    setSubmittingQ(true);
    setQaError("");
    try {
      const id = `qa_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      // Guests can ask without an account — send a typed name (defaults to "Guest").
      const payload = customer
        ? { id, product_id: productId, customer_id: customer.id, question_text: question.trim() }
        : { id, product_id: productId, customer_id: null, customer_name: guestName.trim() || "Guest", question_text: question.trim() };
      const res = await fetch(`${BACKEND_URL}/api/qa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const newQ = await res.json();
        setQas(prev => [{ ...newQ, answers: [] }, ...prev]);
        setQuestion("");
        setGuestName("");
      } else {
        // 403 = the store switched Q&A off, or blocked this person — say so instead of "try again".
        const d = res.status === 403 ? await res.json().catch(() => ({})) : {};
        setQaError(d.error || "Failed to post question. Try again.");
      }
    } catch (e) {
      setQaError("Failed to post question. Try again.");
    } finally {
      setSubmittingQ(false);
    }
  };

  const handleAnswer = async (questionId) => {
    const text = (replyTexts[questionId] || "").trim();
    if (!text || !isOwner) return;
    setSubmittingA(prev => ({ ...prev, [questionId]: true }));
    try {
      const _stok = (()=>{ try { return JSON.parse(localStorage.getItem("bloom:sess")||"null")?.token || ""; } catch { return ""; } })();
      const res = await fetch(`${BACKEND_URL}/api/qa/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-store-id": session.userId, ...(_stok ? { Authorization: `Bearer ${_stok}` } : {}) },
        body: JSON.stringify({ question_id: questionId, answer_text: text, store_id: session.userId, is_store_owner: true }),
      });
      if (res.ok) {
        const newAns = await res.json();
        setQas(prev => prev.map(q => q.id === questionId ? { ...q, answers: [...(q.answers||[]), newAns] } : q));
        setReplyTexts(prev => ({ ...prev, [questionId]: "" }));
      }
    } catch (e) {
      console.error("Error posting answer:", e);
    } finally {
      setSubmittingA(prev => ({ ...prev, [questionId]: false }));
    }
  };

  return (
    <>
      {/* ── Reviews section ── */}
      <div style={{ marginBottom:"40px", paddingTop:"24px", borderTop:"1px solid var(--border)" }}>
        <h2 style={{ fontSize:"20px", fontWeight:800, color:"var(--ink)", marginBottom:"20px" }}>Reviews & Ratings</h2>

        {/* Rating summary */}
        <div style={{ marginBottom:"24px", padding:"20px", background:"var(--teal-light)", borderRadius:"4px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"20px" }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:"36px", fontWeight:800, color:"var(--ink)" }}>{avgRating.toFixed(1)}</div>
              <div style={{ display:"flex", gap:"2px", justifyContent:"center", marginTop:"4px" }}>
                {[1,2,3,4,5].map(i => (
                  <span key={i} style={{ fontSize:"14px", opacity: i <= Math.round(avgRating) ? 1 : 0.3 }}>★</span>
                ))}
              </div>
              <div style={{ fontSize:"12px", color:"var(--ink-soft)", marginTop:"4px" }}>{reviews.length} reviews</div>
            </div>
            <div style={{ flex:1 }}>
              {[5,4,3,2,1].map(star => {
                const count = reviews.filter(r => r.rating === star).length;
                const pct   = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                return (
                  <div key={star} style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
                    <span style={{ fontSize:"12px", color:"var(--ink-soft)", width:"20px" }}>{star}★</span>
                    <div style={{ flex:1, height:"6px", background:"var(--border)", borderRadius:"3px", overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:"var(--pink-btn)" }}/>
                    </div>
                    <span style={{ fontSize:"12px", color:"var(--ink-soft)", width:"30px", textAlign:"right" }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Review form (purchase required — enforced backend-side) */}
        {customer && <ReviewForm productId={productId} customerId={customer.id} storeId={storeId || ""} onReviewAdded={fetchReviews}/>}

        {/* Reviews list */}
        <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
          {reviews.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"var(--ink-soft)", fontSize:"14px" }}>Be the first to review this product! <Icon n="star" size="1.1em" inline /></div>
            : reviews.map(review => <ReviewCard key={review.id} review={review}/>)
          }
        </div>
      </div>

      {/* ── Q&A section ── */}
      {!qaHidden && (
      <>
      <div style={{ marginBottom:"40px", paddingTop:"24px", borderTop:"1px solid var(--border)" }}>
        <h2 style={{ fontSize:"20px", fontWeight:800, color:"var(--ink)", marginBottom:"6px" }}>Questions & Answers</h2>
        <p style={{ fontSize:"13px", color:"var(--ink-soft)", marginBottom:"24px" }}>
          Ask the store owner anything about this product.
        </p>

        {/* Ask a question */}
        <div style={{ marginBottom:"32px", padding:"18px", background:"var(--teal-light)", border:"1px solid var(--teal-mid)", borderRadius:"4px" }}>
          <label style={{ display:"block", fontSize:"13px", fontWeight:700, color:"var(--ink)", marginBottom:"10px" }}>
            Ask a question
          </label>
          {/* Guests can ask without an account — just a name (optional; defaults to "Guest"). */}
          {!customer && (
            <input
              type="text"
              value={guestName}
              onChange={e => setGuestName(e.target.value)}
              placeholder="Your name (optional)"
              style={{ width:"100%", padding:"9px 12px", border:"1px solid var(--border)", borderRadius:"4px", fontSize:"13px", color:"var(--ink)", background:"var(--bg)", boxSizing:"border-box", marginBottom:"8px", fontFamily:"inherit" }}
            />
          )}
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="What would you like to know about this product?"
            rows={3}
            style={{ width:"100%", padding:"10px 12px", border:"1px solid var(--border)", borderRadius:"4px", fontSize:"13px", color:"var(--ink)", background:"var(--bg)", resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }}
          />
          {qaError && <p style={{ fontSize:"12px", color:"var(--red)", margin:"6px 0 0" }}>{qaError}</p>}
          <div style={{ display:"flex", justifyContent:"flex-end", marginTop:"10px" }}>
            <button onClick={handleAskQuestion} disabled={submittingQ || !question.trim()}
              className="btn-teal"
              style={{ padding:"8px 20px", fontSize:"13px", fontWeight:700, opacity: submittingQ || !question.trim() ? 0.5 : 1, cursor: submittingQ || !question.trim() ? "not-allowed" : "pointer" }}>
              {submittingQ ? "Posting…" : "Post Question"}
            </button>
          </div>
        </div>

        {/* Q&A list */}
        {qas.length === 0
          ? <div style={{ padding:"20px", textAlign:"center", color:"var(--ink-soft)", fontSize:"14px" }}>No questions yet — be the first to ask!</div>
          : qas.map(qa => (
            <div key={qa.id} style={{ marginBottom:"24px", borderBottom:"1px solid var(--border)", paddingBottom:"24px" }}>

              {/* Question */}
              <div style={{ display:"flex", gap:"10px", marginBottom:"12px" }}>
                <span style={{ fontSize:"18px", flexShrink:0 }}><Icon n="chat" size="1.1em" inline /></span>
                <div style={{ flex:1 }}>
                  <p style={{ margin:0, fontSize:"14px", color:"var(--ink)", fontWeight:500, lineHeight:1.5 }}>{qa.question_text}</p>
                  <span style={{ fontSize:"11px", color:"var(--ink-soft)" }}>
                    {qa.created_at ? new Date(qa.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : ""}
                  </span>
                </div>
              </div>

              {/* Answers */}
              {(qa.answers || []).length > 0 && (
                <div style={{ marginLeft:"28px", display:"flex", flexDirection:"column", gap:"10px" }}>
                  {qa.answers.map(ans => (
                    <div key={ans.id} style={{ display:"flex", gap:"10px", padding:"12px", background:"var(--teal-light)", borderRadius:"4px", border:"1px solid var(--teal-mid)" }}>
                      <span style={{ fontSize:"16px", flexShrink:0 }}><Icon n="store" size="1.1em" inline /></span>
                      <div style={{ flex:1 }}>
                        <p style={{ margin:"0 0 4px", fontSize:"13px", color:"var(--ink)", lineHeight:1.5 }}>{ans.answer_text}</p>
                        <span style={{ fontSize:"11px", color:"var(--teal)", fontWeight:600 }}>Store Owner</span>
                        {ans.created_at && (
                          <span style={{ fontSize:"11px", color:"var(--ink-soft)", marginLeft:8 }}>
                            · {new Date(ans.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Owner reply input */}
              {isOwner && !(qa.answers || []).length && (
                <div style={{ marginLeft:"28px", marginTop:"10px", display:"flex", gap:"8px" }}>
                  <input
                    type="text"
                    value={replyTexts[qa.id] || ""}
                    onChange={e => setReplyTexts(prev => ({ ...prev, [qa.id]: e.target.value }))}
                    placeholder="Answer this question…"
                    style={{ flex:1, padding:"8px 12px", border:"1px solid var(--border)", borderRadius:"4px", fontSize:"13px", color:"var(--ink)" }}
                    onKeyDown={e => e.key === "Enter" && handleAnswer(qa.id)}
                  />
                  <button onClick={() => handleAnswer(qa.id)} disabled={submittingA[qa.id] || !(replyTexts[qa.id] || "").trim()}
                    className="btn-teal"
                    style={{ padding:"8px 16px", fontSize:"13px", fontWeight:700, opacity: submittingA[qa.id] ? 0.5 : 1 }}>
                    {submittingA[qa.id] ? "…" : "Answer"}
                  </button>
                </div>
              )}

              {/* Owner can also add a second answer to clarify */}
              {isOwner && (qa.answers || []).length > 0 && (
                <div style={{ marginLeft:"28px", marginTop:"8px", display:"flex", gap:"8px" }}>
                  <input
                    type="text"
                    value={replyTexts[qa.id] || ""}
                    onChange={e => setReplyTexts(prev => ({ ...prev, [qa.id]: e.target.value }))}
                    placeholder="Add another reply…"
                    style={{ flex:1, padding:"6px 12px", border:"1px solid var(--border)", borderRadius:"4px", fontSize:"12px", color:"var(--ink-soft)" }}
                    onKeyDown={e => e.key === "Enter" && handleAnswer(qa.id)}
                  />
                  <button onClick={() => handleAnswer(qa.id)} disabled={submittingA[qa.id] || !(replyTexts[qa.id] || "").trim()}
                    style={{ padding:"6px 12px", fontSize:"12px", fontWeight:600, background:"transparent", border:"1px solid var(--border)", borderRadius:"4px", cursor:"pointer", color:"var(--ink-soft)", opacity: submittingA[qa.id] ? 0.5 : 1 }}>
                    {submittingA[qa.id] ? "…" : "Reply"}
                  </button>
                </div>
              )}
            </div>
          ))
        }
      </div>
      </>
      )}
    </>
  );
}
